import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import type {PayloadAction} from '@reduxjs/toolkit'
import {AxiosInstance, AxiosRequestConfig} from 'axios'
import {STATUS} from 'src/constants/constants'
import type {AdherenceLabels, AdherenceSectionsConfig} from 'src/utils/adherenceCharts'

type InitialStateConfig = {
  status: string,
  error: string,
  data: {
    scope: string;
    newStyleEnabled?: boolean;
    theme: Record<string, any>;
    logoPSP: string;
    logoBottomMenuPSP: string;
    sidebarItems: Record<string, any>;
    headerConfiguration: Record<string, any>;
    footerConfiguration: Record<string, any>;
    patientsConfigurations: Record<string, any>;
    patientDetailConfiguration: Record<string, any>;
    agendaConfigurations: Record<string, any>;
    exportConfigurations: Record<string, any>;
    profileConfiguration: Record<string, any>;
    documentsConfigurations: Record<string, any>;
    therapyDiaryConfigurations: Record<string, any>;
    analyticsConfigurations?: {
      filtersConfig?: Array<{id: string; label?: string}>;
      sections?: AdherenceSectionsConfig;
      labels?: Partial<AdherenceLabels>;
    };
    therapyConfigurations: Record<string, any>;
    summaryConfiguration: Record<string, any>;
    virtualMeetingsConfigurations: Record<string, any>;
    infoMaterialsConfigurations: Record<string, any>;
    myDocumentsConfigurations: Record<string, any>;
    echocardiographicRequestsConfigurations: Record<string, any>;
    surveysConfiguration: Record<string, any>;
    patientActivitiesConfigurations: Record<string, any>;
    pharmacovigilanceType: Record<string, any>;
    logoImagePSP: string;
    PSPTitleAndIcon: Record<string, any>;
    digitalEnrollmentConfigurations: Record<string, any>;
    enrollmentHubConfigurations: Record<string, any>;
    firstLoginConfiguration: Record<string, any>;
    biologicalSamplesConfiguration: Record<string, any>;
    communicationsConfigurations: Record<string, any>;
    materialRequestsConfigurations: Record<string, any>;
    customerUtilitiesConfigurations: Record<string, any>;
    medicalCentersConfigurations: Record<string, any>;
    performancesConfigurations: Record<string, any>;
    programEmail?: string;
    homepageConfigurations: {
      desktopImage?: string;
      mobileImage?: string;
      programName?: string;
      cards?: Array<{ visible: boolean; title: string; contentType: string }>;
      greenNumber?: string;
      greenNumberDescription?: string;
      content?: string;
    };
  }
}

export const initialState: InitialStateConfig = {
  status: '',
  data: {
    scope: '',
    theme: {},
    logoPSP: '',
    logoBottomMenuPSP: '',
    logoImagePSP: '',
    sidebarItems: {},
    headerConfiguration: {},
    footerConfiguration: {},
    patientsConfigurations: {},
    patientDetailConfiguration: {},
    agendaConfigurations: {},
    exportConfigurations: {},
    profileConfiguration: {},
    documentsConfigurations: {},
    therapyDiaryConfigurations: {},
    therapyConfigurations: {},
    summaryConfiguration: {},
    virtualMeetingsConfigurations: {},
    infoMaterialsConfigurations: {},
    myDocumentsConfigurations: {},
    echocardiographicRequestsConfigurations: {},
    surveysConfiguration: {},
    patientActivitiesConfigurations: {},
    pharmacovigilanceType: {},
    digitalEnrollmentConfigurations: {},
    enrollmentHubConfigurations: {},
    PSPTitleAndIcon: {},
    firstLoginConfiguration: {},
    biologicalSamplesConfiguration: {},
    communicationsConfigurations: {},
    materialRequestsConfigurations: {},
    customerUtilitiesConfigurations: {},
    medicalCentersConfigurations: {},
    performancesConfigurations: {},
    homepageConfigurations: {}
  },
  error: ''
}

export const fetchPspConfig = createAsyncThunk('psp-config/fetchPspConfig', async ({httpClient, config} : {httpClient: AxiosInstance, config: AxiosRequestConfig}) => {
  return await httpClient.request(config)
})

export const pspConfigSlice = createSlice({
  name: 'psp-config',
  initialState,
  reducers: {
    clearPspConfig: () => initialState,
    updateScope: (state, action: PayloadAction<string>) => {
      state.data.scope = action.payload
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchPspConfig.pending, state => {
      state.status = STATUS.PENDING
    })
    builder.addCase(fetchPspConfig.fulfilled, (state, action: any) => {
      state.status = STATUS.FULFILLED
      state.data = {...state, ...action.payload.data}
      state.error = ''
    })
    builder.addCase(fetchPspConfig.rejected, (state, action) => {
      state.status = STATUS.REJECTED
    })
  }
})

export const {clearPspConfig, updateScope} = pspConfigSlice.actions
export default pspConfigSlice.reducer
